import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { differenceInCalendarDays, differenceInHours, differenceInMinutes, format, isValid, parseISO } from 'date-fns';
import { AlertCircle, Check, CheckCircle2, FileText, ReceiptText, RefreshCw, Settings, ShieldPlus, Truck, Undo2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { AppNotification, NotificationKind } from '@/types/notifications';
import Button from './Button';
import Icon, { IconComponent } from './Icon';
import { useNotifications } from './NotificationsContext';
import SegmentedTabs from './SegmentedTabs';

const KIND: Record<NotificationKind, { icon: IconComponent; tile: string }> = {
  delivery: { icon: Truck, tile: 'tw-bg-ds-info-bg tw-text-ds-info-fg' },
  claim: { icon: FileText, tile: 'tw-bg-ds-brand-tint tw-text-ds-brand-text' },
  protection: { icon: ShieldPlus, tile: 'tw-bg-ds-warning-bg tw-text-ds-warning-fg' },
  return: { icon: Undo2, tile: 'tw-bg-ds-subtle tw-text-ds-text-secondary' },
  plan: { icon: CheckCircle2, tile: 'tw-bg-ds-success-bg tw-text-ds-success-fg' },
  invoice: { icon: ReceiptText, tile: 'tw-bg-ds-subtle tw-text-ds-text-secondary' },
};

/** "12 min ago", "5h ago", "Yesterday", "28 Oct". */
function timeAgo(at: string, now = new Date()) {
  const d = parseISO(at);
  if (!isValid(d)) return '';
  const minutes = differenceInMinutes(now, d);
  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes} min ago`;
  if (differenceInCalendarDays(now, d) === 0) return `${differenceInHours(now, d)}h ago`;
  if (differenceInCalendarDays(now, d) === 1) return 'Yesterday';
  return format(d, 'dd MMM');
}

const isToday = (at: string) => differenceInCalendarDays(new Date(), parseISO(at)) === 0;

const Item: React.FC<{ item: AppNotification; onOpen: (item: AppNotification) => void }> = ({ item, onOpen }) => {
  const { icon, tile } = KIND[item.kind];
  return (
    <li>
      <button
        type="button"
        onClick={() => onOpen(item)}
        className={cn(
          'tw-relative tw-flex tw-w-full tw-items-start tw-gap-3 tw-rounded-xl tw-p-3 tw-text-left tw-transition-colors',
          'hover:tw-bg-ds-subtle focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-ds-brand',
          !item.read && 'tw-bg-ds-subtle',
        )}
      >
        <span className={cn('tw-flex tw-h-9 tw-w-9 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-full', tile)}>
          <Icon icon={icon} size={18} />
        </span>
        <span className="tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-gap-0.5 tw-pr-3">
          <span className="tw-text-[14px] tw-font-semibold tw-leading-5 tw-text-ds-text-primary">{item.title}</span>
          <span className="tw-text-[13px] tw-leading-[18px] tw-text-ds-text-secondary">{item.body}</span>
          <span className="tw-text-[12px] tw-leading-4 tw-text-ds-text-tertiary">{timeAgo(item.at)}</span>
        </span>
        {!item.read && (
          <span className="tw-absolute tw-right-3 tw-top-4 tw-h-2 tw-w-2 tw-rounded-full tw-bg-ds-brand">
            <span className="tw-sr-only">Unread</span>
          </span>
        )}
      </button>
    </li>
  );
};

const Group: React.FC<{ label: string; items: AppNotification[]; onOpen: (item: AppNotification) => void }> = ({ label, items, onOpen }) =>
  items.length ? (
    <section aria-label={label} className="tw-flex tw-flex-col tw-gap-1">
      <h3 className="tw-px-1 tw-pt-1 tw-text-[12px] tw-font-semibold tw-leading-4 tw-text-ds-text-secondary">{label}</h3>
      <ul className="tw-flex tw-flex-col tw-gap-1">
        {items.map((item) => (
          <Item key={item.id} item={item} onOpen={onOpen} />
        ))}
      </ul>
    </section>
  ) : null;

const State: React.FC<{ tone: 'neutral' | 'danger'; title: string; children: React.ReactNode; action?: React.ReactNode }> = ({ tone, title, children, action }) => (
  <div className="tw-flex tw-flex-col tw-items-center tw-gap-2 tw-px-4 tw-py-8 tw-text-center">
    <span
      className={cn(
        'tw-mb-2 tw-flex tw-h-14 tw-w-14 tw-items-center tw-justify-center tw-rounded-full',
        tone === 'danger' ? 'tw-bg-ds-danger-bg tw-text-ds-danger-fg' : 'tw-bg-ds-subtle tw-text-ds-text-secondary',
      )}
    >
      <Icon icon={tone === 'danger' ? AlertCircle : CheckCircle2} size={26} />
    </span>
    <p className="tw-text-[20px] tw-font-bold tw-leading-7 tw-text-ds-text-primary">{title}</p>
    <p className="tw-max-w-[280px] tw-text-[13px] tw-leading-[18px] tw-text-ds-text-secondary">{children}</p>
    {action}
  </div>
);

interface NotificationsPanelProps {
  /** `popover`: the bell's panel (title row + settings footer); `page`: the phone page body. */
  variant: 'popover' | 'page';
  /** Called after opening an item or a link, e.g. to close the popover. */
  onNavigate?: () => void;
}

/** Notification list with All / Unread filter, mark-all-read, and empty / error states (Figma N1, N2, N3, N5). */
const NotificationsPanel: React.FC<NotificationsPanelProps> = ({ variant, onNavigate }) => {
  const navigate = useNavigate();
  const { items, loading, error, reload, markRead, markAllRead, settingsPath } = useNotifications();
  const [filter, setFilter] = useState<'all' | 'unread'>('all');
  const unread = items.filter((n) => !n.read);
  const shown = filter === 'unread' ? unread : items;

  const open = (item: AppNotification) => {
    markRead(item.id);
    if (item.href) {
      navigate(item.href);
      onNavigate?.();
    }
  };

  const settings = settingsPath && (
    <Link
      to={settingsPath}
      onClick={onNavigate}
      aria-label="Notification settings"
      className="tw-flex tw-h-10 tw-w-10 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-xl tw-border tw-border-ds-border-default tw-bg-ds-surface tw-text-ds-text-primary hover:tw-bg-ds-subtle focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-ds-brand"
    >
      <Icon icon={Settings} size={18} />
    </Link>
  );
  const markAll = unread.length > 0 && (
    <Button variant="link" leadingIcon={variant === 'page' ? Check : undefined} onClick={markAllRead}>
      Mark all as read
    </Button>
  );
  const hasList = !loading && !error && items.length > 0;
  const tabs = hasList && (
    <SegmentedTabs
      label="Filter notifications"
      value={filter}
      onChange={setFilter}
      items={[
        { value: 'all', label: `All · ${items.length}` },
        { value: 'unread', label: `Unread · ${unread.length}` },
      ]}
      className="tw-flex-1 [&>button]:tw-flex-1"
    />
  );

  const body = loading ? (
    <div aria-busy className="tw-flex tw-flex-col tw-gap-2 tw-p-1">
      {[0, 1, 2].map((i) => (
        <div key={i} className="tw-h-16 tw-animate-pulse tw-rounded-xl tw-bg-ds-subtle" />
      ))}
    </div>
  ) : error ? (
    <State
      tone="danger"
      title="Couldn’t load notifications"
      action={
        <Button size="sm" variant="secondary" leadingIcon={RefreshCw} className="tw-mt-2" onClick={reload}>
          Try again
        </Button>
      }
    >
      Check your connection and try again.
    </State>
  ) : items.length === 0 ? (
    <State tone="neutral" title="You’re all caught up">
      Updates about your claims, deliveries, returns and plans will show up here.
    </State>
  ) : shown.length === 0 ? (
    <p className="tw-px-4 tw-py-8 tw-text-center tw-text-[13px] tw-leading-[18px] tw-text-ds-text-secondary">No unread notifications.</p>
  ) : (
    <div className="tw-flex tw-flex-col tw-gap-3">
      <Group label="Today" items={shown.filter((n) => isToday(n.at))} onOpen={open} />
      <Group label="Earlier" items={shown.filter((n) => !isToday(n.at))} onOpen={open} />
    </div>
  );

  if (variant === 'page') {
    return (
      <div className="tw-flex tw-flex-col tw-gap-4">
        {(tabs || settings) && (
          <div className="tw-flex tw-items-center tw-gap-2.5">
            {tabs || <span className="tw-flex-1" />}
            {settings}
          </div>
        )}
        {markAll && <div className="tw-self-start">{markAll}</div>}
        <div className="tw-rounded-2xl tw-border tw-border-ds-border-default tw-bg-ds-surface tw-p-2">{body}</div>
      </div>
    );
  }

  return (
    <div className="tw-flex tw-flex-col tw-gap-3">
      <div className="tw-flex tw-items-center tw-gap-3 tw-px-1">
        <h2 className="tw-flex-1 tw-text-[17px] tw-font-semibold tw-leading-6 tw-text-ds-text-primary">Notifications</h2>
        {markAll}
        {settings}
      </div>
      {tabs && <div className="tw-flex">{tabs}</div>}
      <div className="tw-max-h-[min(60vh,520px)] tw-overflow-y-auto">{body}</div>
      {hasList && settingsPath && (
        <Link
          to={settingsPath}
          onClick={onNavigate}
          className="tw-flex tw-items-center tw-justify-center tw-gap-1.5 tw-rounded-lg tw-py-1 tw-text-[13px] tw-font-semibold tw-leading-[18px] tw-text-ds-brand-text hover:tw-underline focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-ds-brand"
        >
          <Icon icon={Settings} size={14} />
          Notification settings
        </Link>
      )}
    </div>
  );
};

export default NotificationsPanel;
